import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../../../config/api";
import { formatDate } from "../../../config/utils";
import EmptyState from "../../../components/EmptyState";
import Loader from "../../../components/Loader";
import PageHeader from "../../../components/PageHeader";

function formatMoney(value) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function isOpenStage(stage) {
  return stage !== "Won" && stage !== "Lost";
}

function isOverdue(deal) {
  if (!deal.nextFollowUpAt || !isOpenStage(deal.stage)) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(deal.nextFollowUpAt) < today;
}

function buildAccounts(deals) {
  const map = new Map();

  for (const deal of deals) {
    const key = (deal.accountName || "").trim().toLowerCase();
    if (!key) continue;

    if (!map.has(key)) {
      map.set(key, {
        name: deal.accountName.trim(),
        totalDeals: 0,
        openDeals: 0,
        totalValue: 0,
        openValue: 0,
        wonValue: 0,
        overdueCount: 0,
        lastActivityAt: null,
      });
    }

    const acc = map.get(key);
    acc.totalDeals += 1;
    acc.totalValue += Number(deal.estimatedValue || 0);

    if (isOpenStage(deal.stage)) {
      acc.openDeals += 1;
      acc.openValue += Number(deal.estimatedValue || 0);
    }
    if (deal.stage === "Won") {
      acc.wonValue += Number(deal.estimatedValue || 0);
    }
    if (isOverdue(deal)) {
      acc.overdueCount += 1;
    }
    if (deal.lastActivityAt) {
      const at = new Date(deal.lastActivityAt);
      if (!acc.lastActivityAt || at > acc.lastActivityAt) {
        acc.lastActivityAt = at;
      }
    }
  }

  return Array.from(map.values()).sort((a, b) => b.openValue - a.openValue);
}

export default function AccountsPage() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiFetch("/api/crm/deals");
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setDeals(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || "Could not load accounts.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const accounts = useMemo(() => buildAccounts(deals), [deals]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter((a) => a.name.toLowerCase().includes(q));
  }, [accounts, search]);

  const viewInPipeline = (accountName) => {
    navigate("/crm/pipeline", { state: { search: accountName } });
  };

  return (
    <div>
      <PageHeader
        title="Accounts"
        subtitle="Companies and organisations in your sales pipeline."
      />

      <div className="crm-toolbar glass-card" style={{ marginBottom: 16 }}>
        <div className="crm-search">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by company name"
            aria-label="Search accounts"
          />
        </div>
        {!loading && (
          <span style={{ fontSize: 13, color: "var(--text-2)", whiteSpace: "nowrap" }}>
            {filtered.length} account{filtered.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="glass-card" style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: "24px 20px" }}>
            {[1, 2, 3, 4].map((i) => (
              <Loader key={i} type="skeleton" height={44} style={{ marginBottom: 10 }} />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 32 }}>
            <EmptyState
              title={search ? "No accounts match your search" : "No accounts yet"}
              subtitle={search ? "Try a different name." : "Create a deal in the Pipeline to see accounts here."}
            />
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--border)" }}>
                {["Company", "Deals", "Open", "Open Value", "Won Value", "Overdue", "Last Activity", ""].map((col) => (
                  <th
                    key={col}
                    style={{
                      padding: "10px 16px",
                      textAlign: col === "Open Value" || col === "Won Value" ? "right" : "left",
                      fontSize: 11,
                      fontWeight: 700,
                      color: "var(--text-2)",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((account) => (
                <tr
                  key={account.name}
                  style={{ borderBottom: "1px solid var(--border)" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-2)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "")}
                >
                  <td style={{ padding: "12px 16px", fontWeight: 600, fontSize: 14 }}>
                    {account.name}
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: 14, color: "var(--text-2)" }}>
                    {account.totalDeals}
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: 14 }}>
                    {account.openDeals > 0 ? (
                      <span style={{ color: "var(--brand)", fontWeight: 600 }}>{account.openDeals}</span>
                    ) : (
                      <span style={{ color: "var(--text-2)" }}>0</span>
                    )}
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: 14, textAlign: "right", fontWeight: 600 }}>
                    {formatMoney(account.openValue)}
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: 14, textAlign: "right", color: "var(--success)", fontWeight: account.wonValue > 0 ? 600 : 400 }}>
                    {formatMoney(account.wonValue)}
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: 14 }}>
                    {account.overdueCount > 0 ? (
                      <span style={{ color: "var(--danger)", fontWeight: 600 }}>{account.overdueCount}</span>
                    ) : (
                      <span style={{ color: "var(--text-2)" }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "var(--text-2)" }}>
                    {account.lastActivityAt ? formatDate(account.lastActivityAt) : "—"}
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <button
                      className="btn-ghost"
                      style={{ fontSize: 12, whiteSpace: "nowrap" }}
                      onClick={() => viewInPipeline(account.name)}
                    >
                      View in Pipeline
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
